import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { scrollToTop } from '../utils/scrollUtils';

/**
 * Component that automatically scrolls to top when the route changes.
 *
 * Pages are lazy-loaded and fetch their data asynchronously, so a single
 * scroll on the pathname change can fire before the new page's content has
 * grown the document, leaving the window scrolled where the previous page
 * was. To handle that we disable the browser's automatic scroll restoration
 * and re-assert the top position across the next couple of paints.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Stop the browser from restoring the previous page's scroll position.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Instant scroll for route changes to feel responsive.
    scrollToTop(false);

    // Re-assert after paint in case the page's content (lazy chunk / async
    // data) renders after this effect and shifts the scroll position.
    const raf1 = requestAnimationFrame(() => {
      scrollToTop(false);
      requestAnimationFrame(() => scrollToTop(false));
    });

    return () => cancelAnimationFrame(raf1);
  }, [pathname]);

  return null;
};

export default ScrollToTop;
